import type { PlaybookOutputFile } from '@doneverse/shared'
import { api } from '../api/index.js'

interface SignedUrlResponse {
  uploadUrl: string
  fileUrl: string
  expiresAt: string
}

function getFileExtension(contentType: string): string {
  const subtype = contentType.split('/')[1]?.split(';')[0]?.trim()
  return subtype || 'webm'
}

export function uploadToSignedUrl(
  file: File,
  uploadUrl: string,
  onProgress?: (percent: number) => void,
  contentType?: string,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', contentType || file.type || 'application/octet-stream')

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Upload failed')))
    xhr.send(file)
  })
}

export async function getSignedUploadUrl(
  fileName: string,
  contentType: string,
  workspacePlaybookId: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, workspacePlaybookId }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

export async function getSignedUploadUrlForProjectTask(
  fileName: string,
  contentType: string,
  projectTaskId: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, projectTaskId }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

export async function getSignedUploadUrlForLogo(
  fileName: string,
  contentType: string,
  userId?: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, type: 'logo', userId }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

export async function uploadFile(
  file: File,
  workspacePlaybookId: string,
  onProgress?: (percent: number) => void,
): Promise<PlaybookOutputFile> {
  const contentType = file.type || 'application/octet-stream'
  const { uploadUrl, fileUrl } = await getSignedUploadUrl(file.name, contentType, workspacePlaybookId)
  await uploadToSignedUrl(file, uploadUrl, onProgress, contentType)

  return {
    name: file.name,
    url: fileUrl,
    size: file.size,
    type: contentType,
  }
}

export async function uploadFileForProjectTask(
  file: File,
  projectTaskId: string,
  onProgress?: (percent: number) => void,
): Promise<PlaybookOutputFile> {
  const contentType = file.type || 'application/octet-stream'
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForProjectTask(file.name, contentType, projectTaskId)
  await uploadToSignedUrl(file, uploadUrl, onProgress, contentType)

  return {
    name: file.name,
    url: fileUrl,
    size: file.size,
    type: contentType,
  }
}

export async function getSignedUploadUrlForMessage(
  fileName: string,
  contentType: string,
  scopeId: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, type: 'message', scopeId }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

export async function uploadVoiceMessage(
  blob: Blob,
  scopeId: string,
  onProgress?: (percent: number) => void,
): Promise<{ fileUrl: string; size: number; fileName: string; contentType: string }> {
  const contentType = blob.type || 'audio/webm'
  const fileName = `voice-${Date.now()}.${getFileExtension(contentType)}`
  const file = new File([blob], fileName, { type: contentType })
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForMessage(fileName, contentType, scopeId)
  await uploadToSignedUrl(file, uploadUrl, onProgress, contentType)
  return { fileUrl, size: blob.size, fileName, contentType }
}

export async function uploadMessageFile(
  file: File,
  scopeId: string,
  onProgress?: (percent: number) => void,
): Promise<{ fileUrl: string; size: number; fileName: string; contentType: string }> {
  const contentType = file.type || 'application/octet-stream'
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForMessage(file.name, contentType, scopeId)
  await uploadToSignedUrl(file, uploadUrl, onProgress, contentType)
  return { fileUrl, size: file.size, fileName: file.name, contentType }
}

export async function getSignedUploadUrlForFeedback(
  fileName: string,
  contentType: string,
  scopeId?: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, type: 'feedback', scopeId }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

/**
 * Screenshot on a feedback submission or in a feedback thread.
 *
 * `scopeId` is the feedback item's id when replying in-thread. A submission-form
 * upload happens before the item exists, so it's omitted and the API scopes the
 * object by uploader instead.
 */
export async function uploadFeedbackFile(
  file: File,
  scopeId?: string,
  onProgress?: (percent: number) => void,
): Promise<{ fileUrl: string; size: number; fileName: string; contentType: string }> {
  const contentType = file.type || 'application/octet-stream'
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForFeedback(file.name, contentType, scopeId)
  await uploadToSignedUrl(file, uploadUrl, onProgress, contentType)
  return { fileUrl, size: file.size, fileName: file.name, contentType }
}

export async function getSignedUploadUrlForProfilePicture(
  fileName: string,
  contentType: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, type: 'profile_picture' }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

export async function uploadProfilePicture(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<string> {
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForProfilePicture(file.name, file.type)
  await uploadToSignedUrl(file, uploadUrl, onProgress)
  return fileUrl
}

export async function uploadLogoFile(
  file: File,
  userId?: string,
  onProgress?: (percent: number) => void,
): Promise<string> {
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForLogo(file.name, file.type, userId)
  await uploadToSignedUrl(file, uploadUrl, onProgress)
  return fileUrl
}

/**
 * Founder onboarding Q2.5 — brand assets (logo, style guide, visual
 * guidelines). Many files per founder, stored under `founders/{id}/brand`, and
 * distinct from `uploadLogoFile` above, which sets a single `logo_url` on the
 * user. The API restricts the accepted content types; see
 * `BRAND_ASSET_CONTENT_TYPES` in apps/api/src/routes/uploads.ts.
 */
export async function getSignedUploadUrlForBrandAsset(
  fileName: string,
  contentType: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, type: 'brand_asset' }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

export async function uploadBrandAsset(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<PlaybookOutputFile> {
  const contentType = file.type || 'application/octet-stream'
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForBrandAsset(file.name, contentType)
  await uploadToSignedUrl(file, uploadUrl, onProgress, contentType)

  return { name: file.name, url: fileUrl, size: file.size, type: contentType }
}

/**
 * A doer's introduction video for their profile (onboarding step 9).
 *
 * Doers only, for themselves — the API has no admin-on-behalf path. Unlike
 * `uploadProfilePicture`, nothing is written to the record here: a doer may
 * record several takes and keep one, so `PATCH /v1/doer-profiles/me` is what
 * publishes the URL this returns.
 */
export async function getSignedUploadUrlForDoerIntroVideo(
  fileName: string,
  contentType: string,
): Promise<SignedUrlResponse> {
  return api
    .post({ fileName, contentType, type: 'doer_intro_video' }, '/v1/uploads/signed-url')
    .json<SignedUrlResponse>()
}

export async function uploadDoerIntroVideo(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<string> {
  const contentType = file.type || 'video/mp4'
  const { uploadUrl, fileUrl } = await getSignedUploadUrlForDoerIntroVideo(file.name, contentType)
  await uploadToSignedUrl(file, uploadUrl, onProgress, contentType)
  return fileUrl
}
